import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpEventType, HttpResponse } from '@angular/common/http';
import { forkJoin, of, timer, Subscription } from 'rxjs';
import { catchError, switchMap, take, takeWhile } from 'rxjs/operators';

import { ApiService } from '../../../../core/services/api.service';
import { S3FileEntry, FileStatus, ApprovalStatus } from '../../../../core/models/file-browser.models';
import { CreateFolderDialogComponent } from './dialogs/create-folder-dialog.component';
import { ConfirmDeleteDialogComponent } from './dialogs/confirm-delete-dialog.component';
import {
  UploadOptionsDialogComponent,
  UploadOptions,
} from './dialogs/upload-options-dialog.component';
import {
  BatchActionConfirmDialogComponent,
  BatchActionType,
} from './dialogs/batch-action-confirm-dialog.component';
import {
  VersionConflictDialogComponent,
  ConflictEntry,
} from './dialogs/version-conflict-dialog.component';
import { FileHistoryDialogComponent } from './dialogs/file-history-dialog.component';

const SUPPORTED_EXTENSIONS = new Set(['.pdf', '.tif', '.tiff', '.png', '.jpg', '.jpeg', '.txt', '.md']);
const TEXT_EXTENSIONS = new Set(['.txt', '.md']);

interface ActiveUpload {
  filename: string;
  percent: number;
  error: string | null;
}

interface EnrichedFile extends S3FileEntry {
  status: FileStatus | undefined;
  approvalStatus: ApprovalStatus | null;
  confidence: number | null;
  parsedS3Key: string | null;
  hasNewerParsed: boolean;
}

@Component({
  selector: 'app-file-browser',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CreateFolderDialogComponent,
    ConfirmDeleteDialogComponent,
    UploadOptionsDialogComponent,
    BatchActionConfirmDialogComponent,
    VersionConflictDialogComponent,
    FileHistoryDialogComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './file-browser.component.html',
})
export class FileBrowserComponent implements OnInit, OnDestroy {
  @ViewChild('versionInput') versionInput!: ElementRef<HTMLInputElement>;
  @Output() notification = new EventEmitter<string>();
  @Output() viewParsedFile = new EventEmitter<string>();

  private api = inject(ApiService);
  private cdr = inject(ChangeDetectorRef);

  // Navigation
  currentPrefix = '';
  folders: string[] = [];
  enrichedFiles: EnrichedFile[] = [];
  loading = false;
  loadError: string | null = null;

  // Selection
  selected = new Set<string>();

  // Status
  statusLoading = false;

  // Upload
  pendingFiles: File[] = [];
  pendingVersionTargetKey: string | null = null;
  pendingIsVersionReplace = false;
  activeUpload: ActiveUpload | null = null;
  uploadError: string | null = null;
  isDragging = false;

  // Dialogs
  showCreateFolder = false;
  showDeleteConfirm = false;
  showUploadOptions = false;
  showVersionConflict = false;
  versionConflicts: ConflictEntry[] = [];
  pendingBatchAction: { type: BatchActionType; keys: string[] } | null = null;
  folderToDelete: string | null = null;
  folderDeleteError: string | null = null;
  historyFile: S3FileEntry | null = null;
  menuDeleteFile: S3FileEntry | null = null;
  kbDeleteFile: S3FileEntry | null = null;
  kbDeleteInProgress = false;
  kbDeleteError: string | null = null;
  private kbDeletePollSub: Subscription | null = null;

  // Poll the async KB-delete job every 3s, up to ~3 minutes, then give up
  // waiting and just refresh (the worker keeps running regardless).
  private static readonly KB_DELETE_POLL_MS = 3000;
  private static readonly KB_DELETE_MAX_POLLS = 60;
  openMenuKey: string | null = null;
  menuPosition: { top: number; right: number } | null = null;
  menuPendingAction: { type: 'parse' | 'ingest' | 'reingest'; file: EnrichedFile } | null = null;

  get breadcrumbs(): { label: string; prefix: string }[] {
    const crumbs: { label: string; prefix: string }[] = [{ label: 'Root', prefix: '' }];
    const parts = this.currentPrefix.replace(/\/$/, '').split('/').filter(Boolean);
    let path = '';
    for (const part of parts) {
      path += part + '/';
      crumbs.push({ label: part, prefix: path });
    }
    return crumbs;
  }

  get selectedCount(): number {
    return this.selected.size;
  }

  get allSelected(): boolean {
    return this.enrichedFiles.length > 0 && this.enrichedFiles.every(f => this.selected.has(f.key));
  }

  get selectedFiles(): EnrichedFile[] {
    return this.enrichedFiles.filter(f => this.selected.has(f.key));
  }

  get selectedHasIngested(): boolean {
    return this.selectedFiles.some(f => f.status === 'INGESTED');
  }

  get hasNonTextSelected(): boolean {
    return this.selectedFiles.some(f => !this.isTextFile(f.key));
  }

  get canBatchParse(): boolean {
    return this.selectedFiles.length > 0 && this.selectedFiles.every(
      f => !this.isTextFile(f.key) && f.status === 'UPLOADED'
    );
  }

  get canBatchIngest(): boolean {
    const files = this.selectedFiles;
    if (!files.length) return false;
    const allText = files.every(f => this.isTextFile(f.key));
    if (allText) return files.some(f => f.status !== 'INGESTED');
    const allNonText = files.every(f => !this.isTextFile(f.key));
    if (allNonText) return files.every(f => f.status === 'PARSED');
    return false;
  }

  ngOnInit(): void {
    this.loadFolder('');
  }

  loadFolder(prefix: string): void {
    this.currentPrefix = prefix;
    this.selected.clear();
    this.loading = true;
    this.loadError = null;
    this.cdr.markForCheck();

    this.api.listFiles(prefix).subscribe({
      next: listing => {
        this.folders = listing.folders;
        this.enrichedFiles = listing.files.map(f => ({
          ...f,
          status: undefined,
          approvalStatus: null,
          confidence: null,
          parsedS3Key: null,
          hasNewerParsed: false,
        }));
        this.loading = false;
        this.cdr.markForCheck();
        this.loadStatuses();
      },
      error: err => {
        this.loadError = err?.message || 'Failed to load folder';
        this.loading = false;
        this.cdr.markForCheck();
      },
    });
  }

  private loadStatuses(): void {
    if (!this.enrichedFiles.length) return;
    this.statusLoading = true;
    this.cdr.markForCheck();

    const fileRefs = this.enrichedFiles.map(f => ({ s3Key: f.key, versionId: f.versionId }));
    this.api.getFileStatuses(fileRefs).subscribe({
      next: statuses => {
        const statusByKey = new Map(statuses.map(s => [s.s3Key, s]));
        this.enrichedFiles = this.enrichedFiles.map(f => {
          const s = statusByKey.get(f.key);
          return s
            ? { ...f, status: s.status, approvalStatus: s.approvalStatus ?? null, confidence: s.parseConfidence ?? null, parsedS3Key: s.parsedS3Key ?? null, hasNewerParsed: s.hasNewerParsed ?? false }
            : f;
        });
        this.statusLoading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.statusLoading = false;
        this.cdr.markForCheck();
      },
    });
  }

  formatConfidence(v: number | null): string {
    return v === null ? '' : `${v.toFixed(1)}%`;
  }

  parsedStatusLabel(approvalStatus: ApprovalStatus | null): string {
    switch (approvalStatus) {
      case 'manually_approved': return 'Parsed · Manually Approved';
      case 'auto_approved':     return 'Parsed · Auto-approved';
      case 'needs_review':      return 'Parsed · Requires Review';
      default:                  return 'Parsed';
    }
  }

  parsedStatusClasses(approvalStatus: ApprovalStatus | null): string {
    switch (approvalStatus) {
      case 'manually_approved': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300';
      case 'auto_approved':     return 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300';
      case 'needs_review':      return 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300';
      default:                  return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
    }
  }

  menuViewParsedFile(file: EnrichedFile): void {
    this.openMenuKey = null;
    this.menuPendingAction = null;
    this.menuPosition = null;
    if (file.parsedS3Key) {
      this.viewParsedFile.emit(file.parsedS3Key);
    }
    this.cdr.markForCheck();
  }

  navigateTo(prefix: string): void {
    this.loadFolder(prefix);
  }

  toggleSelect(key: string): void {
    if (this.selected.has(key)) {
      this.selected.delete(key);
    } else {
      this.selected.add(key);
    }
    this.cdr.markForCheck();
  }

  toggleSelectAll(): void {
    if (this.allSelected) {
      this.selected.clear();
    } else {
      this.enrichedFiles.forEach(f => this.selected.add(f.key));
    }
    this.cdr.markForCheck();
  }

  folderName(folderPrefix: string): string {
    const stripped = folderPrefix.replace(/\/$/, '');
    return stripped.split('/').pop() ?? folderPrefix;
  }

  fileBasename(key: string): string {
    return key.split('/').pop() ?? key;
  }

  fileExtension(key: string): string {
    const name = this.fileBasename(key);
    const dot = name.lastIndexOf('.');
    return dot >= 0 ? name.slice(dot).toLowerCase() : '';
  }

  isTextFile(key: string): boolean {
    return TEXT_EXTENSIONS.has(this.fileExtension(key));
  }

  formatBytes(bytes: number): string {
    if (bytes >= 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    if (bytes >= 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return bytes + ' B';
  }

  // ── Drag/drop zone ─────────────────────────────────────────────────────────

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = true;
    this.cdr.markForCheck();
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    const container = event.currentTarget as Element;
    if (!container.contains(event.relatedTarget as Node)) {
      this.isDragging = false;
      this.cdr.markForCheck();
    }
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;
    const files = Array.from(event.dataTransfer?.files ?? []);
    this.handleFileSelection(files);
  }

  onFileInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = '';
    this.handleFileSelection(files);
  }

  private handleFileSelection(files: File[]): void {
    const valid = files.filter(f => SUPPORTED_EXTENSIONS.has(this.fileExtension(f.name)));
    if (!valid.length) {
      this.uploadError = 'No supported file types selected (.pdf, .tif, .tiff, .png, .jpg, .jpeg, .txt, .md)';
      this.cdr.markForCheck();
      return;
    }
    this.uploadError = null;
    this.pendingFiles = valid;

    const existingKeys = new Set(this.enrichedFiles.map(f => f.key));
    const conflicts: ConflictEntry[] = valid
      .filter(f => existingKeys.has((this.currentPrefix + f.name).replace(/^\//, '')))
      .map(f => {
        const key = (this.currentPrefix + f.name).replace(/^\//, '');
        return { filename: f.name, status: this.enrichedFiles.find(ef => ef.key === key)?.status };
      });

    if (conflicts.length) {
      this.versionConflicts = conflicts;
      this.showVersionConflict = true;
    } else if (valid.every(f => this.isTextFile(f.name))) {
      this.uploadFiles(valid, { parse: false, ingest: false });
    } else {
      this.showUploadOptions = true;
    }
    this.cdr.markForCheck();
  }

  onVersionConflictReplace(): void {
    this.showVersionConflict = false;
    this.versionConflicts = [];
    this.pendingIsVersionReplace = true;
    this.showUploadOptions = true;
    this.cdr.markForCheck();
  }

  onVersionConflictCancel(): void {
    this.showVersionConflict = false;
    this.versionConflicts = [];
    this.pendingFiles = [];
    this.pendingIsVersionReplace = false;
    this.cdr.markForCheck();
  }

  onUploadOptionsConfirmed(options: UploadOptions): void {
    this.showUploadOptions = false;
    const files = [...this.pendingFiles];
    const versionTargetKey = this.pendingVersionTargetKey;
    this.pendingFiles = [];
    this.pendingVersionTargetKey = null;
    this.pendingIsVersionReplace = false;
    this.uploadFiles(files, options, versionTargetKey);
  }

  onUploadOptionsCancelled(): void {
    this.showUploadOptions = false;
    this.pendingFiles = [];
    this.pendingVersionTargetKey = null;
    this.pendingIsVersionReplace = false;
    this.cdr.markForCheck();
  }

  onVersionFileInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = '';
    if (!files.length || !this.pendingVersionTargetKey) return;

    const file = files[0];
    if (!SUPPORTED_EXTENSIONS.has(this.fileExtension(file.name))) {
      this.uploadError = 'Unsupported file type (.pdf, .tif, .tiff, .png, .jpg, .jpeg, .txt, .md)';
      this.cdr.markForCheck();
      return;
    }
    this.uploadError = null;
    this.pendingFiles = [file];
    this.pendingIsVersionReplace = true;
    if (this.isTextFile(file.name)) {
      this.uploadFiles([file], { parse: false, ingest: false }, this.pendingVersionTargetKey);
      this.pendingVersionTargetKey = null;
      this.pendingIsVersionReplace = false;
    } else {
      this.showUploadOptions = true;
    }
    this.cdr.markForCheck();
  }

  private uploadFiles(files: File[], options: UploadOptions, versionTargetKey: string | null = null): void {
    this.uploadError = null;
    const upload = (file: File) => {
      const ext = this.fileExtension(file.name);
      const s3Key = versionTargetKey ?? (this.currentPrefix + file.name).replace(/^\//, '');
      this.activeUpload = { filename: file.name, percent: 0, error: null };
      this.cdr.markForCheck();

      this.api.requestFilePresignedUpload(s3Key, file.name, file.size).subscribe({
        next: presign => {
          console.log('requestFilePresignedUpload', presign);
          this.api.uploadFileToS3(presign.url, file).subscribe({
            next: event => {
              if (event.type === HttpEventType.UploadProgress) {
                const total = event.total ?? file.size;
                this.activeUpload = {
                  filename: file.name,
                  percent: total > 0 ? Math.round((event.loaded / total) * 100) : 0,
                  error: null,
                };
                this.cdr.markForCheck();
              } else if (event.type === HttpEventType.Response) {
                const resp = event as HttpResponse<unknown>;
                const versionId = resp.headers.get('x-amz-version-id');
                this.api
                  .confirmFileUpload({
                    s3Key,
                    versionId,
                    filename: file.name,
                    sizeBytes: file.size,
                    parse: options.parse && !TEXT_EXTENSIONS.has(ext),
                    ingest: options.ingest,
                  })
                  .subscribe({
                    next: () => {
                      this.activeUpload = null;
                      this.cdr.markForCheck();
                      this.loadFolder(this.currentPrefix);
                      const isParse = options.parse && !TEXT_EXTENSIONS.has(ext);
                      if (isParse && options.ingest) {
                        this.notification.emit(`"${file.name}" uploaded — parse & ingest queued`);
                      } else if (isParse) {
                        this.notification.emit(`"${file.name}" uploaded — parsing queued`);
                      } else if (options.ingest) {
                        this.notification.emit(`"${file.name}" uploaded — ingestion queued`);
                      }
                    },
                    error: err => this.setUploadError(err),
                  });
              }
            },
            error: err => this.setUploadError(err),
          });
        },
        error: err => this.setUploadError(err),
      });
    };

    files.reduce(
      (chain, file) => chain.then(() => new Promise<void>(resolve => {
        upload(file);
        setTimeout(resolve, 200);
      })),
      Promise.resolve()
    );
  }

  private setUploadError(err: unknown): void {
    this.activeUpload = null;
    this.uploadError = (err as Error)?.message || 'Upload failed';
    this.cdr.markForCheck();
  }

  // ── Folder creation / deletion ───────────────────────────────────────────────

  onCreateFolderConfirmed(name: string): void {
    this.showCreateFolder = false;
    const path = this.currentPrefix + name;
    this.api.createFolder(path).subscribe({
      next: () => this.loadFolder(this.currentPrefix),
      error: err => {
        this.loadError = err?.message || 'Failed to create folder';
        this.cdr.markForCheck();
      },
    });
  }

  requestDeleteFolder(folderPath: string): void {
    this.folderDeleteError = null;
    this.folderToDelete = folderPath;
    this.cdr.markForCheck();
  }

  onFolderDeleteConfirmed(): void {
    const path = this.folderToDelete!;
    this.folderToDelete = null;
    this.folderDeleteError = null;

    this.api.deleteFolder(path).subscribe({
      next: () => this.loadFolder(this.currentPrefix),
      error: err => {
        this.folderDeleteError = err?.message || 'Failed to delete folder';
        this.cdr.markForCheck();
      },
    });
  }

  // ── Delete ──────────────────────────────────────────────────────────────────

  requestDelete(): void {
    if (!this.selected.size || this.selectedHasIngested) return;
    this.showDeleteConfirm = true;
    this.cdr.markForCheck();
  }

  onDeleteConfirmed(): void {
    this.showDeleteConfirm = false;
    const toDelete = [...this.selected];
    this.selected.clear();

    const calls = toDelete.map(key => {
      const file = this.enrichedFiles.find(f => f.key === key);
      return this.api.deleteFile({ s3Key: key, versionId: file?.versionId ?? null }).pipe(
        catchError(() => of(null))
      );
    });

    forkJoin(calls).subscribe(() => this.loadFolder(this.currentPrefix));
  }

  // ── Selection ────────────────────────────────────────────────────────────────

  clearSelection(): void {
    this.selected.clear();
    this.cdr.markForCheck();
  }

  // ── Batch actions (parse / ingest / move) ────────────────────────────────────

  openBatchAction(type: BatchActionType): void {
    const keys = [...this.selected];
    if (!keys.length) return;
    if ((type === 'move') && this.selectedHasIngested) return;

    this.pendingBatchAction = { type, keys };
    this.cdr.markForCheck();
  }

  onBatchActionConfirmed(destPath: string): void {
    const action = this.pendingBatchAction;
    this.pendingBatchAction = null;
    if (!action) return;

    if (action.type === 'parse') {
      const refs = this.enrichedFiles
        .filter(f => action.keys.includes(f.key))
        .map(f => ({ s3Key: f.key, versionId: f.versionId, sizeBytes: f.size }));
      if (refs.length) {
        this.api.parseFiles(refs).subscribe({ error: () => {} });
        this.notification.emit(`Parsing queued for ${refs.length} file${refs.length > 1 ? 's' : ''}`);
      }
    } else if (action.type === 'ingest') {
      const refs = this.enrichedFiles
        .filter(f => action.keys.includes(f.key))
        .map(f => ({ s3Key: f.key, versionId: f.versionId, sizeBytes: f.size }));
      this.api.ingestFiles(refs).subscribe({ error: () => {} });
      this.notification.emit(`Ingestion queued for ${refs.length} file${refs.length > 1 ? 's' : ''}`);
    } else if (action.type === 'move') {
      const dest = destPath.replace(/\/$/, '') + '/';
      const calls = action.keys.map(key => {
        const basename = this.fileBasename(key);
        return this.api.moveFile({ sourceKey: key, destKey: dest + basename }).pipe(
          catchError(() => of(null))
        );
      });
      this.selected.clear();
      forkJoin(calls).subscribe(() => this.loadFolder(this.currentPrefix));
    }
    this.cdr.markForCheck();
  }

  // ── File context menu ───────────────────────────────────────────────────────

  @HostListener('document:click')
  onDocumentClick(): void {
    if (this.openMenuKey) {
      this.openMenuKey = null;
      this.menuPendingAction = null;
      this.menuPosition = null;
      this.cdr.markForCheck();
    }
  }

  toggleMenu(key: string, event: MouseEvent): void {
    event.stopPropagation();
    if (this.openMenuKey === key) {
      this.openMenuKey = null;
      this.menuPendingAction = null;
      this.menuPosition = null;
    } else {
      const btn = event.currentTarget as HTMLElement;
      const rect = btn.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      this.menuPosition = {
        top: spaceBelow > 240 ? rect.bottom + 4 : rect.top - 244,
        right: window.innerWidth - rect.right,
      };
      this.openMenuKey = key;
      this.menuPendingAction = null;
    }
    this.cdr.markForCheck();
  }

  menuStageAction(type: 'parse' | 'ingest' | 'reingest', file: EnrichedFile, event: MouseEvent): void {
    event.stopPropagation();
    this.openMenuKey = null;
    this.menuPosition = null;
    this.menuPendingAction = { type, file };
    this.cdr.markForCheck();
  }

  menuConfirmAction(): void {
    const pending = this.menuPendingAction!;
    this.openMenuKey = null;
    this.menuPendingAction = null;
    this.menuPosition = null;

    const ref = { s3Key: pending.file.key, versionId: pending.file.versionId, sizeBytes: pending.file.size };
    const name = this.fileBasename(pending.file.key);

    if (pending.type === 'parse') {
      this.api.parseFiles([ref]).subscribe({ error: () => {} });
      this.notification.emit(`Parsing queued for "${name}"`);
    } else if (pending.type === 'ingest') {
      this.api.ingestFiles([ref]).subscribe({ error: () => {} });
      this.notification.emit(`Ingestion queued for "${name}"`);
    } else if (pending.type === 'reingest') {
      this.api.ingestFiles([ref]).subscribe({ error: () => {} });
      this.notification.emit(`Re-ingestion queued for "${name}"`);
    }
    this.cdr.markForCheck();
  }

  menuUploadNewVersion(file: EnrichedFile): void {
    this.openMenuKey = null;
    this.menuPendingAction = null;
    this.menuPosition = null;
    this.pendingVersionTargetKey = file.key;
    this.versionInput.nativeElement.click();
    this.cdr.markForCheck();
  }

  menuShowHistory(file: EnrichedFile): void {
    this.openMenuKey = null;
    this.menuPendingAction = null;
    this.menuPosition = null;
    this.historyFile = file;
    this.cdr.markForCheck();
  }

  menuRequestDelete(file: EnrichedFile): void {
    this.openMenuKey = null;
    this.menuPendingAction = null;
    this.menuPosition = null;
    this.menuDeleteFile = file;
    this.cdr.markForCheck();
  }

  onMenuDeleteConfirmed(): void {
    const file = this.menuDeleteFile!;
    this.menuDeleteFile = null;
    this.api.deleteFile({ s3Key: file.key, versionId: file.versionId ?? null })
      .pipe(catchError(() => of(null)))
      .subscribe(() => this.loadFolder(this.currentPrefix));
  }

  // ── Delete from knowledge base ───────────────────────────────────────────────

  menuRequestKbDelete(file: EnrichedFile): void {
    this.openMenuKey = null;
    this.menuPendingAction = null;
    this.menuPosition = null;
    this.kbDeleteError = null;
    this.kbDeleteFile = file;
    this.cdr.markForCheck();
  }

  onKbDeleteConfirmed(): void {
    const file = this.kbDeleteFile!;
    this.kbDeleteFile = null;
    this.kbDeleteInProgress = true;
    this.kbDeleteError = null;
    this.cdr.markForCheck();

    // The graph removal runs asynchronously on the worker (it can take
    // minutes). The request returns 202 with a DELETE job — poll it until the
    // worker finishes, keeping the spinner up so the file leaves the KB before
    // we refresh.
    this.api.deleteFromKnowledgeBase({ s3Key: file.key }).subscribe({
      next: res => this.pollKbDeleteJob(res.job.id),
      error: err => {
        this.kbDeleteInProgress = false;
        this.kbDeleteError = err?.message || 'Failed to delete from knowledge base';
        this.cdr.markForCheck();
      },
    });
  }

  private pollKbDeleteJob(jobId: string): void {
    this.kbDeletePollSub?.unsubscribe();
    this.kbDeletePollSub = timer(
      FileBrowserComponent.KB_DELETE_POLL_MS,
      FileBrowserComponent.KB_DELETE_POLL_MS,
    )
      .pipe(
        take(FileBrowserComponent.KB_DELETE_MAX_POLLS),
        switchMap(() =>
          this.api.getIngestionJob(jobId).pipe(catchError(() => of(null))),
        ),
        // Keep polling while the job is still pending/running (or a poll
        // failed); emit the terminal state once, then complete.
        takeWhile(
          job => !job || (job.status !== 'DONE' && job.status !== 'FAILED'),
          true,
        ),
      )
      .subscribe({
        next: job => {
          if (job?.status === 'DONE') {
            this.finishKbDelete();
          } else if (job?.status === 'FAILED') {
            this.finishKbDelete(job.statusMessage || 'Knowledge-base removal failed');
          }
        },
        // Reached only when the poll budget is exhausted without a terminal
        // status (a terminal one unsubscribes in `next` before completing) —
        // stop waiting and refresh anyway; the worker will finish on its own.
        complete: () => this.finishKbDelete(),
      });
  }

  private finishKbDelete(errorMessage?: string): void {
    this.kbDeletePollSub?.unsubscribe();
    this.kbDeletePollSub = null;
    this.kbDeleteInProgress = false;
    this.kbDeleteError = errorMessage ?? null;
    this.loadFolder(this.currentPrefix);
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.kbDeletePollSub?.unsubscribe();
  }
}
